import { AfterViewChecked, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonDataService } from 'src/sb-tools/typescript/services/common-data.service';
import { HelperService } from 'src/sb-tools/typescript/services/helper.service';
import { TicketSource } from '@sb-tools/typescript/enums/ticket-source.enum';
import { ReplyStoreService } from 'src/app/core/services/ticket/reply-store.service';
import { Subject } from 'rxjs/internal/Subject';
import { ReplyMentionsToCodePipe } from '../../../../shared/pipes/reply-mentions-to-code.pipe';
import { ReplyService } from 'src/app/core/services/ticket/reply.service';
import { AttachmentType } from 'src/sb-tools/typescript/enums/attachment-type.enum';
import { SmilesService } from 'src/sb-tools/typescript/services/smiles.service';
import { ReplySortingService } from 'src/app/core/services/ticket/reply-sorting.service';
import { ReplyUpdateModel } from '@sb-tools/typescript/models/reply-update-model.model';
import { AttachmentBase } from '@sb-tools/typescript/models/attachment-base.model';
import { TicketDetailsComponent } from 'src/app/ticket-details/ticket-details.component';
import { ReplyTemplateService } from '../../../../../sb-modules/Frontend-Reply-Templates-Module/core/reply-template.service';
import { SignatureService } from '@sb-tools/typescript/services/signature.service';
import { DetailedTicketDTO } from '../../../../core/models/dto/detailed-ticket.dto';
import { QuillReplyTemplatesComponent } from '../../../../../sb-modules/Frontend-Reply-Templates-Module/quill-reply-templates/quill-reply-templates.component';
import { SbQuillMentionComponent } from '../../../../shared/components/sb-quill-mention/sb-quill-mention.component';
import { QuillEditorComponent } from 'ngx-quill';
import { FormsModule } from '@angular/forms';
import { QuillEmojiPickerComponent } from '../../../../../sb-tools/typescript/components/quill-emoji-picker/quill-emoji-picker.component';
import { InlineSVGComponent } from '../../../../../sb-modules/Frontend-Inline-SVG-Module/inline-svg.component';
import { FileViewComponent } from '../../../../../sb-modules/Frontend-Document-Viewer-Module/components/file-view/file-view.component';
import { NgClass } from '@angular/common';
import { FilePreviewComponent } from '../../../../../sb-modules/Frontend-Document-Viewer-Module/components/file-preview/file-preview.component';
import { GiphyPickerComponent } from '../../../../../sb-modules/Frontend-GIF-Module/giphy-picker/giphy-picker.component';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { IsInvalidMessagePipe } from '../../../../../sb-tools/typescript/pipes/ticket/is-invalid-message.pipe';
import { FilterContentAttachmentsPipe } from '../../../../shared/pipes/filter-content-attachments.pipe';

@Component({
    selector: 'app-new-reply-social-media',
    templateUrl: './new-reply-social-media.component.html',
    styleUrls: ['./new-reply-social-media.component.scss'],
    imports: [
        QuillReplyTemplatesComponent,
        SbQuillMentionComponent,
        QuillEditorComponent,
        FormsModule,
        QuillEmojiPickerComponent,
        InlineSVGComponent,
        FileViewComponent,
        NgClass,
        FilePreviewComponent,
        GiphyPickerComponent,
        TooltipModule,
        TranslateModule,
        IsInvalidMessagePipe,
        FilterContentAttachmentsPipe,
    ],
})
export class NewReplySocialMediaComponent implements OnInit, OnDestroy, AfterViewChecked {
    @ViewChild('btnSignature') btnSignature: ElementRef;

    @Input() newReply: ReplyUpdateModel;
    @Input() parent: TicketDetailsComponent;
    @Input() parentReply?;
    @Input() ticket: DetailedTicketDTO;

    attachmentTypeEnum = AttachmentType;
    autoSignatureHTMLContent: string;
    isOnlyAutoSignatureSet = false;
    quillConfig = {
        toolbar: false,
    };
    quillEditor;
    ticketSourceEnum = TicketSource;
    usersToMention;

    private unsubscriber = new Subject<void>();

    constructor(
        public commonDataService: CommonDataService,
        public replyTemplateService: ReplyTemplateService,
        private signatureService: SignatureService,
        private smilesService: SmilesService,
        private replyStoreService: ReplyStoreService,
        private replyMentionsToCodePipe: ReplyMentionsToCodePipe,
        private replyService: ReplyService,
        private replySortingService: ReplySortingService,
    ) {}

    ngOnInit() {
        if (this.parentReply) {
            const tempSubReply = this.replyStoreService.getTempReply(this.ticket.id, this.parentReply.id);
            if (tempSubReply) {
                this.newReply.message.text = tempSubReply.text;
                this.newReply.message.html = tempSubReply.html;
            }
            setTimeout(() => {
                const isAutoSignature = this.signatureService.getSignatureStatus(this.ticket.source);
                if (
                    !this.newReply.message.text &&
                    !this.replyStoreService.isReplyHasOnlyInlineImage(this.newReply.message.html) &&
                    isAutoSignature
                ) {
                    this.parent.setSignature(
                        this.newReply,
                        this.parentReply ? this.parentReply.id : null,
                        { target: this.btnSignature },
                        false,
                    );
                }
            }, 100);
        }
    }

    ngAfterViewChecked() {
        if (!this.parentReply) {
            this.replyService.initializeNewReplyEditor();
        }
    }

    addGifAttachment(gif: AttachmentBase) {
        this.newReply.attachments.push(gif);
    }

    deleteGifAttachment(attachment: AttachmentBase) {
        const index = this.newReply.attachments.indexOf(attachment);
        this.newReply.attachments.splice(index, 1);
    }

    onEditorCreated(quill) {
        this.quillEditor = quill;
    }

    addAutoSignature() {
        if (this.ticket.facebookUsersToMention) {
            this.newReply.message.html = this.autoSignatureHTMLContent;
            this.newReply.message.text = this.replyMentionsToCodePipe.transform(this.autoSignatureHTMLContent);
        }

        this.newReply.message.text = HelperService.strip(
            this.smilesService.replaceImagesToCodes(this.autoSignatureHTMLContent),
            false,
        );
    }

    onReplyChanged(data, reply: ReplyUpdateModel) {
        this.isOnlyAutoSignatureSet = data.html && !reply.message.html;

        if (this.isOnlyAutoSignatureSet) {
            this.autoSignatureHTMLContent = data.html;
        }

        if (this.ticket.facebookUsersToMention) {
            reply.message.text = this.replyMentionsToCodePipe.transform(reply.message.html);
        }

        reply.message.text = HelperService.strip(this.smilesService.replaceImagesToCodes(reply.message.html), false);

        if (this.parentReply) {
            this.replyStoreService.saveTempReply(this.newReply.message, this.parentReply.id);
        } else {
            this.replyStoreService.saveTempReply(this.parent.newReply.message);

            this.replyService.controlVisibilityOfNewReplyField(this.replySortingService.replySortBy);
        }
    }

    ngOnDestroy() {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
